/**
 * Define qué roles permiten abrir una página dentro de una aplicación. La aplicación
 * decide primero si exige iniciar sesión.
 *
 * Una lista vacía permite el acceso a cualquiera que pueda abrir la aplicación y se
 * muestra como Todos. Una lista con roles exige sesión y al menos uno de ellos. Solo
 * admin mantiene la página reservada a quien la construye. withPageAdmin añade admin a
 * las páginas limitadas para que sigan disponibles en la vista previa del constructor.
 */
import { DEFAULT_ICON, iconName } from "./icons.ts";
import { ADMIN_ROLE } from "./people.ts";
import type { PageRecord } from "./types.ts";

/**
 * Lo mínimo que hace falta saber de una página para decidir quien la abre.
 *
 * Un separador no tiene roles: es el título de un grupo y se ve cuando quien
 * mira puede abrir alguna página del grupo (`visibleMenu`). Uno guardado con
 * roles de antes se lee como si no los tuviera.
 */
type PageGate = Pick<PageRecord, "roles"> & Partial<Pick<PageRecord, "separator">>;

/** Los roles que cuentan: los de una página; un separador no tiene. */
const gateRoles = (page: PageGate): string[] => (page.separator ? [] : (page.roles ?? []));

/**
 * La lista de roles de una página limitada, con `admin` siempre delante.
 *
 * Quien construye mira sus propias páginas con el rol `admin` --es donde
 * arranca la vista previa-- y una página limitada que no lo nombre lo deja
 * fuera de su propia aplicación. Por eso en la pantalla `admin` sale marcado y
 * no se puede desmarcar, y por eso lo que se guarda dice lo mismo que lo que
 * se ve.
 *
 * La lista vacía se queda vacía: no hay estado guardado de `Todos`, y añadirle
 * `admin` la convertiría en "solo quien construye", que es otro estado --uno
 * que se pide apagando `Todos`, no pasando por aquí--.
 */
export function withPageAdmin(roles: string[]): string[] {
  if (!roles.length) return [];
  return [ADMIN_ROLE, ...roles.filter((r) => r !== ADMIN_ROLE)];
}

/** Si la página limita quien la abre a ciertos roles. */
export function pageIsLimited(page: PageGate): boolean {
  return gateRoles(page).length > 0;
}

/**
 * Si quien mira puede abrir la página. `viewer` es `null` cuando entró sin
 * sesión; sus `roles` son los que tiene en esta aplicación.
 *
 * La exigencia de sesión de la aplicación no se mira aquí: es una frontera que
 * tapa a todas sus páginas por igual y se comprueba antes, donde se sabe que
 * aplicación es. Ver `runPageData` y `publicBundle`.
 */
export function canOpenPage(page: PageGate, viewer: { roles: string[] } | null): boolean {
  const roles = gateRoles(page);
  if (!roles.length) return true;
  if (!viewer) return false;
  return roles.some((r) => viewer.roles.includes(r));
}

/**
 * El menú tal como lo ve quien mira: las páginas que puede abrir y, de los
 * separadores, solo los que encabezan alguna de ellas. Un título sin nada
 * debajo que abrir no se enseña. Conserva el orden en que llegan.
 */
export function visibleMenu<T extends PageGate & Pick<PageRecord, "id" | "order">>(
  pages: T[],
  viewer: { roles: string[] } | null,
): T[] {
  const keep = new Set<string>();
  let heading: T | null = null;
  // El grupo se decide en el orden del menú, aunque la lista llegue en otro.
  for (const page of [...pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
    if (page.separator) {
      heading = page;
      continue;
    }
    if (!canOpenPage(page, viewer)) continue;
    keep.add(page.id);
    if (heading) keep.add(heading.id);
  }
  return pages.filter((page) => keep.has(page.id));
}

/** Por que no se puede abrir: por falta de sesión o por falta de rol. */
export function pageDenial(page: PageGate, viewer: { roles: string[] } | null): "auth" | "role" {
  return !viewer && pageIsLimited(page) ? "auth" : "role";
}

/* ------------------------------------------------------------------ */
/* El nombre de la página                                               */
/* ------------------------------------------------------------------ */

/** Lo más largo que puede ser un nombre de página puesto por la IA. */
const PAGE_NAME_MAX = 32;

/**
 * El nombre con el que nace una página recién creada.
 *
 * Es un nombre de relleno --dice el número de página, no de que va-- y por eso
 * se reconoce: mientras siga puesto, la IA puede cambiarlo por uno que cuente
 * lo que se le pidió construir.
 */
export function defaultPageName(order: number): string {
  return `Página ${order + 1}`;
}

/**
 * Si la página todavía se llama como nació.
 *
 * Solo entonces se le cambia el nombre solo: uno puesto a mano es una decisión
 * de quien construye y no se toca, aunque la página se reescriba entera.
 */
export function isDefaultPageName(name: string | undefined | null): boolean {
  const flat = (name ?? "").trim();
  return /^p[áa]gina\s+\d+$/i.test(flat) || flat === HOME_PAGE_NAME;
}

/**
 * El nombre con el que nace la página de inicio de toda aplicación.
 *
 * Es tan de relleno como "Página 2": lo pone la plataforma al crear la
 * aplicación, no quien construye, y sin contarlo la primera pantalla que se
 * pide --casi siempre en esa página-- se quedaba sin el nombre que la IA le
 * daría a cualquier otra.
 */
export const HOME_PAGE_NAME = "Inicio";

/**
 * Deja un nombre de página en condiciones de guardarse, o `null` si lo que
 * llegó no sirve.
 *
 * Se recorta a una línea, se le quitan las comillas con las que un modelo suele
 * envolverlo y se corta por palabra: un nombre largo no cabe en el sidebar, y
 * cortarlo a media palabra se lee peor que cortarlo antes.
 */
export function cleanPageName(value: unknown): string | null {
  const flat = String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^["'“”«»]+|["'“”«»]+$/g, "")
    .trim();
  if (!flat || isDefaultPageName(flat)) return null;
  if (flat.length <= PAGE_NAME_MAX) return flat;

  const cut = flat.slice(0, PAGE_NAME_MAX);
  const space = cut.lastIndexOf(" ");
  return (space > 12 ? cut.slice(0, space) : cut).trim();
}

/* ------------------------------------------------------------------ */
/* El icono de la página                                              */
/* ------------------------------------------------------------------ */

/**
 * El icono con el que nace una página recién creada.
 *
 * Es relleno igual que el nombre --un archivo genérico no dice de que va la
 * pantalla-- y por eso se reconoce: mientras siga puesto, la IA puede cambiarlo
 * por uno que hable de lo que la página tiene dentro.
 */
export const DEFAULT_PAGE_ICON = "file-01";

/**
 * Si la página todavía lleva el icono con el que nació.
 *
 * Uno elegido a mano en el selector es una decisión de quien construye y no se
 * toca, igual que el nombre. Una página sin icono guardado cuenta como recién
 * nacida: no hay nada que perder.
 */
export function isDefaultPageIcon(icon: string | undefined | null): boolean {
  const flat = (icon ?? "").trim();
  // También en el formato del paquete de antes (`File01Icon`), que es como
  // nacía la página de inicio de las aplicaciones creadas hasta ahora.
  return !flat || flat === DEFAULT_PAGE_ICON || iconName(flat) === DEFAULT_PAGE_ICON;
}

/**
 * Deja un icono de página en condiciones de guardarse, o `null` si lo que llegó
 * no sirve.
 *
 * Lo que no esté en la fuente se descarta en vez de caer en el icono por
 * defecto: un nombre inventado no da error --dibuja un hueco-- y guardarlo
 * convertido en otro icono cualquiera sería elegir por el modelo. Si no se
 * reconoce, la página se queda con el que tenía.
 */
export function cleanPageIcon(value: unknown): string | null {
  const flat = String(value ?? "")
    .trim()
    .replace(/^["'“”«»]+|["'“”«»]+$/g, "")
    .replace(/^(?:icon|hgi)-/, "")
    .trim();
  if (!flat) return null;

  const name = iconName(flat);
  /* `iconName` devuelve el icono por defecto ante un nombre que no existe: eso
     es lo que se descarta aquí, salvo que el nombre pedido sea justo ese. */
  if (name === DEFAULT_ICON && flat !== DEFAULT_ICON) return null;
  return name;
}
